import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactInputMask from 'react-input-mask';
import { useField } from '@unform/core';

import { CalendarIcon } from '../../../assets/svg/SVGComponents';
import {
  Container,
  InputAndErrorWrapper,
  InputError,
} from '../../Input/styles';
import {
  DATETIME_MASK,
  localToMasked,
  maskedToLocal,
} from '../../../utils/tournament/dateTimeInput';

interface IDateTimeInputProps {
  /** Campo do unform. O valor lido é AAAA-MM-DDTHH:MM, como no datetime-local. */
  name: string;
  readOnly?: boolean;
  'aria-label'?: string;
}

/**
 * Data e hora em DD/MM/AAAA HH:MM (24h), independente do idioma do
 * navegador. Para o formulário, o valor continua no formato local
 * AAAA-MM-DDTHH:MM; data incompleta ou impossível vira INVALID_DATETIME, para
 * a validação mostrar o erro.
 */
const DateTimeInput: React.FC<IDateTimeInputProps> = ({
  name,
  readOnly = false,
  'aria-label': ariaLabel,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { fieldName, defaultValue, error, registerField } = useField(name);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(!!defaultValue);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: ref => maskedToLocal(ref.current?.value ?? ''),
      setValue: (ref, value: string) => {
        if (ref.current) ref.current.value = localToMasked(value);
      },
      clearValue: ref => {
        if (ref.current) ref.current.value = '';
      },
    });
  }, [fieldName, registerField]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setIsFilled(!!maskedToLocal(inputRef.current?.value ?? ''));
  }, []);

  return (
    <InputAndErrorWrapper>
      {error && <InputError>{error}</InputError>}
      <Container
        $isFocused={isFocused}
        $isFilled={isFilled}
        $readOnly={readOnly}
        onClick={() => !readOnly && inputRef.current?.focus()}
      >
        <CalendarIcon
          aria-hidden
          width="1.25rem"
          height="1.25rem"
          stroke="#9E9E9E"
          strokeWidth={1.75}
          fill="none"
          style={{ flex: 'none', marginRight: '0.5rem' }}
        />
        <ReactInputMask
          mask={DATETIME_MASK}
          defaultValue={localToMasked(defaultValue)}
          readOnly={readOnly}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
        >
          {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
            <input
              {...inputProps}
              // O react-input-mask tira readOnly das props do filho: repassar
              // o mesmo valor aqui é o que de fato trava o input.
              readOnly={readOnly}
              ref={inputRef}
              name={name}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="DD/MM/AAAA HH:MM"
              aria-label={ariaLabel}
              aria-invalid={!!error}
            />
          )}
        </ReactInputMask>
      </Container>
    </InputAndErrorWrapper>
  );
};

export default DateTimeInput;
